import { Box, Typography } from '@mui/material';

import { Alert } from '@components/alert/Alert';
import { Dialog } from '@components/dialog/Dialog';
import { FormControlLabel } from '@components/formControlLabel/FormControlLabel';
import { Switch } from '@components/switch/Switch';
import { AlertType } from '@pages/PortalAdmin/GlobalAlertManagement/GlobalAlerts.constants';
import { useApiEnvStore } from '@utils/api-client';
import { getIsFeatureFlagEnabled, getToggleableFlags } from '@utils/featureFlags';
import { shallow } from 'zustand/shallow';

type FeatureToggleModalProps = {
  open: boolean;
  onClose: () => void;
};

export const FeatureToggleModal = ({ open, onClose }: FeatureToggleModalProps) => {
  const [featureFlagOverrides, setFeatureFlagOverride] = useApiEnvStore(
    (state) => [state.featureFlagOverrides, state.setFeatureFlagOverride],
    shallow,
  );

  const toggleableFlags = getToggleableFlags();

  return (
    <Dialog
      open={open}
      handleCloseDialog={onClose}
      title='Feature Toggles'
      size='small'
      secondaryBtnText='Close'
      secondaryBtnAction={onClose}
    >
      <Box>
        <Alert id='feature-toggle-alert' type={AlertType.Info}>
          Items with * require you to refresh your page after toggling.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 3, py: 1 }}>
          {toggleableFlags.map(({ flag, label, description }) => {
            const isEnabled =
              flag in featureFlagOverrides
                ? featureFlagOverrides[flag]
                : getIsFeatureFlagEnabled(flag, { isInternalUser: false });

            return (
              <Box key={flag}>
                <FormControlLabel
                  label={label}
                  labelPlacement='start'
                  name={flag}
                  sxProps={{ formControlLabel: { justifyContent: 'space-between', width: '100%' } }}
                  typographyProps={{ sx: { fontWeight: 'normal' } }}
                  control={
                    <Switch
                      size='small'
                      variant='primary'
                      checked={isEnabled}
                      onChange={(_, checked) => setFeatureFlagOverride(flag, checked)}
                    />
                  }
                />
                <Typography variant='caption'>{description}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};
